import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Copy, Check, Zap, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

export function CrmBridgePage() {
  const { currentLocation: authLocation } = useAuth();
  const currentLocation = authLocation || JSON.parse(localStorage.getItem('current_location') || 'null');
  const [copied, setCopied] = useState('');

  const ghlLocationId = currentLocation?.ghl_location_id || 'YOUR_GHL_LOCATION_ID';
  const crmBaseUrl = currentLocation?.crm_base_url || 'https://app.gohighlevel.com';
  const appUrl = process.env.REACT_APP_BACKEND_URL || 'https://your-app.com';

  const parentScript = `<!-- OPTSC CRM Navigation Bridge -->
<script>
(function() {
  function getLocId() {
    var m = window.location.pathname.match(/\\/v2\\/location\\/([^\\/]+)/);
    return m ? m[1] : '';
  }

  function navigateTo(path) {
    var full = window.location.origin + path;
    try {
      window.history.pushState({}, '', full);
      window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
      window.dispatchEvent(new CustomEvent('routeChangeEvent', { detail: { path: path } }));
    } catch(e) {}
    setTimeout(function() {
      var current = window.location.pathname + window.location.search;
      if (current.indexOf(path.split('?')[0]) === -1) {
        window.location.assign(full);
      }
    }, 300);
  }

  // Send GHL location ID to embedded iframe so app auto-switches location
  function syncLocationToIframe() {
    var locId = getLocId();
    var iframes = document.querySelectorAll('iframe');
    for (var i = 0; i < iframes.length; i++) {
      try {
        iframes[i].contentWindow.postMessage({
          source: 'ghl-bridge',
          action: 'location-sync',
          ghlLocationId: locId
        }, '*');
      } catch(e) {}
    }
  }

  // Poll for location changes (GHL URL changes when switching locations)
  var lastLocId = getLocId();
  setInterval(function() {
    var current = getLocId();
    if (current && current !== lastLocId) {
      lastLocId = current;
      syncLocationToIframe();
    }
  }, 1000);

  // Also sync on initial load after iframe renders
  setTimeout(syncLocationToIframe, 2000);

  window.addEventListener('message', function(e) {
    var d = e.data;
    if (!d || d.source !== 'optsc-app') return;
    var locId = getLocId();

    if (d.action === 'open-contact' && d.contactId && locId) {
      navigateTo('/v2/location/' + locId + '/contacts/detail/' + d.contactId);
    }
    else if (d.action === 'open-inbox' && d.contactId && locId) {
      var path = d.conversationId
        ? '/v2/location/' + locId + '/conversations/conversations/' + d.conversationId + '?category=team-inbox&tab=all'
        : '/v2/location/' + locId + '/conversations/conversations/?category=team-inbox&tab=all&query=' + encodeURIComponent(d.contactName || d.contactId);
      navigateTo(path);
    }
    else if (d.action === 'navigate' && d.path) {
      navigateTo(d.path);
    }
    else if (d.action === 'open-external' && d.url) {
      window.open(d.url, d.target || '_blank');
    }
    // App requesting current location
    else if (d.action === 'get-location') {
      syncLocationToIframe();
    }
    // App changed location — navigate GHL to the new sub-account
    else if (d.action === 'change-location' && d.ghlLocationId) {
      var targetLocId = d.ghlLocationId;
      var currentLocId = getLocId();
      if (targetLocId !== currentLocId) {
        // Replace the location ID in the current URL path to stay on the same page
        var currentPath = window.location.pathname;
        if (currentLocId && currentPath.indexOf(currentLocId) !== -1) {
          var newPath = currentPath.replace(currentLocId, targetLocId);
          window.location.href = window.location.origin + newPath + window.location.search;
        } else {
          window.location.href = window.location.origin + '/v2/location/' + targetLocId + '/dashboard';
        }
      }
    }
  });

  var routeChangeEvent = null;
  function initRouteChange(ev) { routeChangeEvent = ev; }
  window.addEventListener('routeChangeEvent', initRouteChange);

  // Forward browser back/forward to iframe
  window.addEventListener('popstate', function() {
    var iframes = document.querySelectorAll('iframe');
    for (var i = 0; i < iframes.length; i++) {
      try {
        iframes[i].contentWindow.postMessage({
          source: 'ghl-bridge',
          action: 'navigate-back'
        }, '*');
      } catch(e) {}
    }
  });

})();
</script>`;

  // Simple URL — app handles auth + restores last visited route on iframe reload
  // (so switching between GHL menu links preserves the user's position in the app)
  const menuLinkUrl = `${appUrl}?embedded=1`;

  const handleCopy = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied(id);
    toast.success('Copied');
    setTimeout(() => setCopied(''), 2000);
  };

  const CopyBtn = ({ text, id }) => (
    <Button variant="outline" size="sm" className="h-8 gap-1 flex-shrink-0" onClick={() => handleCopy(text, id)}>
      {copied === id ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
    </Button>
  );

  return (
    <div className="bg-white rounded-xl border max-w-4xl mx-auto" data-testid="crm-bridge-page">
      <div className="px-6 py-4 border-b">
        <h1 className="text-lg font-bold text-slate-800">CRM Navigation Bridge</h1>
        <p className="text-xs text-slate-400 mt-0.5">Embed the app inside CRM with seamless in-app navigation</p>
      </div>

      <div className="p-6 space-y-6">
        {/* Step 1 */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge className="bg-slate-800 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full p-0">1</Badge>
            <h2 className="text-sm font-bold text-slate-800">Create Custom Menu Link in CRM</h2>
          </div>
          <p className="text-xs text-slate-500 ml-8">Settings → Custom Menu Links → Create New</p>
          <div className="ml-8 space-y-2">
            <div>
              <label className="text-[10px] font-semibold text-slate-400 uppercase mb-1 block">URL</label>
              <div className="flex gap-2">
                <Input value={menuLinkUrl} readOnly className="h-8 text-xs bg-slate-50 flex-1 font-mono" />
                <CopyBtn text={menuLinkUrl} id="url" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Select: <strong>Open link within the current window as an embedded page (iFrame)</strong></p>
            </div>
          </div>
        </div>

        {/* Step 2 */}
        <div className="space-y-3 border-t pt-6">
          <div className="flex items-center gap-2">
            <Badge className="bg-slate-800 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full p-0">2</Badge>
            <h2 className="text-sm font-bold text-slate-800">Inject Bridge Script into CRM</h2>
          </div>
          <p className="text-xs text-slate-500 ml-8">
            Sub-Account Settings → Business Profile → Custom Scripts (Header). Paste this script.
            It enables "Open Patient Profile", "Go to Inbox" etc. to navigate <strong>within GHL</strong> instead of opening new tabs.
          </p>
          <div className="ml-8">
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] font-semibold text-slate-400 uppercase">Bridge Script</label>
              <CopyBtn text={parentScript} id="script" />
            </div>
            <Textarea value={parentScript} readOnly className="font-mono text-[10px] min-h-[180px] bg-slate-900 text-green-400 border-slate-700 resize-none" />
          </div>
        </div>

        {/* How it works */}
        <div className="space-y-3 border-t pt-6">
          <div className="flex items-center gap-2">
            <Badge className="bg-slate-800 text-white text-xs w-6 h-6 flex items-center justify-center rounded-full p-0">3</Badge>
            <h2 className="text-sm font-bold text-slate-800">What Gets Bridged</h2>
          </div>
          <div className="ml-8 grid grid-cols-2 gap-2">
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-blue-50 border border-blue-100">
              <Zap className="h-3.5 w-3.5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-[11px]">
                <p className="font-semibold text-blue-700">Open Patient Profile</p>
                <p className="text-blue-600 mt-0.5">SPA navigation to contact detail — no refresh</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-emerald-50 border border-emerald-100">
              <Zap className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="text-[11px]">
                <p className="font-semibold text-emerald-700">Go to Inbox</p>
                <p className="text-emerald-600 mt-0.5">Opens conversation thread — smooth route change</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 border border-amber-100">
              <Zap className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="text-[11px]">
                <p className="font-semibold text-amber-700">Open Juvonno</p>
                <p className="text-amber-600 mt-0.5">Named window (Juvonno blocks iframe)</p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-2.5 rounded-lg bg-purple-50 border border-purple-100">
              <Zap className="h-3.5 w-3.5 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="text-[11px]">
                <p className="font-semibold text-purple-700">Bidirectional Location Sync</p>
                <p className="text-purple-600 mt-0.5">App ↔ CRM: switching location in either direction auto-syncs the other</p>
              </div>
            </div>
          </div>
          <p className="ml-8 text-[10px] text-slate-400">Uses pushState + popstate + CRM routeChangeEvent for smooth SPA navigation. Works across all pages.</p>
        </div>

        {/* Config */}
        <div className="border-t pt-4">
          <h3 className="text-[10px] font-bold text-slate-400 uppercase mb-2">Current Location Config</h3>
          <div className="grid grid-cols-3 gap-2 text-[11px]">
            <div className="p-2 rounded bg-slate-50"><span className="text-slate-400">CRM ID:</span> <span className="font-mono text-slate-700">{ghlLocationId}</span></div>
            <div className="p-2 rounded bg-slate-50"><span className="text-slate-400">CRM URL:</span> <span className="font-mono text-slate-700">{crmBaseUrl}</span></div>
            <div className="p-2 rounded bg-slate-50"><span className="text-slate-400">App:</span> <span className="font-mono text-slate-700">{appUrl}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CrmBridgePage;
